import { StudentSubscription } from 'modules/arena/subscriptions/types';
import { SUBSCRIPTION_STATUS_LABELS } from 'modules/arena/subscriptions/content';
import { Card } from 'shared/components/ui/Card';
import { Badge } from 'shared/components/ui/Badge';
import { Button } from 'shared/components/ui/Button';
import { formatBRL } from 'shared/utils/format';
import { RENEWAL_MODE_LABELS } from 'shared/constants/labels';

interface SubscriptionDetailPanelProps {
  subscription: StudentSubscription;
  onChargeNow: (id: string) => void;
  onCancel: (id: string) => void;
  onReactivate: (id: string) => void;
}

const STATUS_TONE = {
  ativa: 'success',
  pendente_pagamento: 'warning',
  cancelada: 'danger',
  expirada: 'neutral',
} as const;

const PAYMENT_TONE = {
  pago: 'success',
  pendente: 'warning',
  falhou: 'danger',
} as const;

export function SubscriptionDetailPanel({
  subscription,
  onChargeNow,
  onCancel,
  onReactivate,
}: SubscriptionDetailPanelProps) {
  const isCancelled = subscription.status === 'cancelada' || subscription.status === 'expirada';

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">Assinatura</p>
          <h3 className="mt-1 font-heading text-lg font-semibold text-ink">{subscription.studentName}</h3>
          <p className="text-sm text-ink-secondary">
            {subscription.classGroupName} · {subscription.sport}
          </p>
        </div>
        <Badge tone={STATUS_TONE[subscription.status]}>{SUBSCRIPTION_STATUS_LABELS[subscription.status]}</Badge>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs text-ink-secondary">Valor mensal</p>
          <p className="font-medium text-ink">R$ {subscription.monthlyPrice}</p>
        </div>
        <div>
          <p className="text-xs text-ink-secondary">Renovação</p>
          <p className="font-medium text-ink">{RENEWAL_MODE_LABELS[subscription.renewalMode]}</p>
        </div>
        <div>
          <p className="text-xs text-ink-secondary">Assinante desde</p>
          <p className="font-medium text-ink">{subscription.startedAtLabel}</p>
        </div>
        <div>
          <p className="text-xs text-ink-secondary">Próxima cobrança</p>
          <p className="font-medium text-ink">{isCancelled ? '—' : subscription.nextBillingLabel}</p>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-ink-secondary">
          Histórico de pagamentos
        </p>
        <div className="mt-2 flex flex-col gap-2">
          {subscription.paymentHistory.map((payment) => (
            <div key={payment.id} className="flex items-center justify-between text-sm">
              <span className="text-ink-secondary">{payment.dateLabel}</span>
              <div className="flex items-center gap-2">
                <span className="text-ink">{formatBRL(payment.amount)}</span>
                <Badge tone={PAYMENT_TONE[payment.status]} className="capitalize">
                  {payment.status}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        {subscription.status === 'pendente_pagamento' && (
          <Button className="flex-1" onClick={() => onChargeNow(subscription.id)}>
            Cobrar agora
          </Button>
        )}

        {isCancelled ? (
          <Button variant="ghost" className="flex-1" onClick={() => onReactivate(subscription.id)}>
            Reativar assinatura
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="flex-1 border-danger text-danger hover:bg-danger-soft"
            onClick={() => onCancel(subscription.id)}
          >
            Cancelar assinatura
          </Button>
        )}
      </div>
    </Card>
  );
}
